import { t } from '../i18n.js';
import { apiGet } from '../api.js';
import { runningTimerSummary, formatDuration, remainingMsFromEndsAt } from '../pages/timers.js';

// Mirrors design D9 / pages/timers.js exactly: 25 s poll (paused while the
// tab is hidden, immediate on focus/visible-again), 1 Hz local tick for the
// countdown display. This poll+tick pair is deliberately independent of the
// /timers page's own poll+tick (see the header comment on `timerIndicator`
// below for why running both concurrently on /timers is the chosen
// trade-off) — but each is its own interval pair with its own teardown, so
// neither can leak into the other.
const POLL_INTERVAL_MS = 25000;
const TICK_INTERVAL_MS = 1000;

/**
 * Global running-timer indicator for the shared `.app-header` (mounted by
 * `mountLayout()` in `components/layout.js` — see the app-navigation spec
 * delta for the `timers` change). Shows the count of currently-running
 * household timers and the soonest one's remaining time, ticking at 1 Hz;
 * hidden entirely when nothing is running. Tapping navigates to `/timers`.
 *
 * Fed by its own 25 s poll of `GET /api/v1/timers` — the same cadence
 * pages/timers.js uses, but NOT the same interval instance, because the
 * indicator lives in the header across every screen (including screens where
 * the timers page isn't mounted) while the page's poll only exists while
 * `/timers` itself is mounted. Running both concurrently while the user is
 * actually on `/timers` means two independent 25 s fetches instead of one —
 * accepted as the simpler-and-still-correct option (both are fully cleaned
 * up, see `destroy()`) over introducing a shared cross-module poll source
 * just to save one background fetch every 25 s.
 *
 * `mountLayout()` is called repeatedly over the app's lifetime (login,
 * logout, locale switch, profile update) and rebuilds the header DOM from
 * scratch each time — removing the old indicator's DOM node does NOT stop
 * its intervals. Callers MUST call `destroy()` on the previous instance
 * before creating a new one, or every remount leaks another live 25 s poll +
 * 1 Hz tick forever (the exact class of leak flagged in the 4.3 review).
 *
 * @returns {{ el: HTMLElement, destroy: () => void }}
 */
export function timerIndicator() {
  const el = document.createElement('a');
  el.href = '#/timers';
  el.dataset.role = 'timer-indicator';
  el.className = [
    'btn-ghost text-xs sm:text-sm',
    '!px-2 gap-1 hidden',
  ].join(' ');

  const icon = document.createElement('span');
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = '⏲️';

  const label = document.createElement('span');
  label.dataset.role = 'timer-indicator-label';

  el.append(icon, label);

  /** @type {Array<object>} */
  let timers = [];

  // Finds the running timer with the smallest remaining time, so its `symbol`
  // can be shown as a compact prefix (spec "where space permits"). Kept as a
  // local helper rather than widening `runningTimerSummary`'s return shape,
  // which stays a pure { count, soonestMs } pair used by existing tests.
  function soonestRunningTimer(nowMs) {
    let soonest = null;
    let soonestRemaining = null;
    for (const timer of timers) {
      if (timer.status !== 'running') continue;
      const rem = remainingMsFromEndsAt(timer.ends_at, nowMs);
      const clamped = rem === null ? 0 : Math.max(0, rem);
      if (soonestRemaining === null || clamped < soonestRemaining) {
        soonestRemaining = clamped;
        soonest = timer;
      }
    }
    return soonest;
  }

  function render(nowMs = Date.now()) {
    const { count, soonestMs } = runningTimerSummary(timers, nowMs);
    if (count === 0) {
      el.classList.add('hidden');
      return;
    }
    const remaining = formatDuration(soonestMs ?? 0);
    const soonest = soonestRunningTimer(nowMs);
    // textContent only — `symbol` is user-supplied and must never be parsed
    // as markup. Compact by design: at most one extra emoji glyph.
    const symbolPrefix = soonest && soonest.symbol ? `${soonest.symbol} ` : '';
    label.textContent = `${symbolPrefix}${count} · ${remaining}`;
    el.setAttribute('aria-label', t('timers.indicator_aria', { count: String(count), remaining }));
    el.classList.remove('hidden');
  }

  async function fetchTimers() {
    try {
      const result = await apiGet('/timers');
      timers = (result && Array.isArray(result.timers)) ? result.timers : [];
      render();
    } catch {
      // Silently fail — the indicator is a non-critical enhancement (mirrors reminderBell).
    }
  }

  function tick() {
    render();
  }

  function pollRefresh() {
    if (document.visibilityState === 'hidden') return;
    fetchTimers();
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'visible') pollRefresh();
  }

  function onFocus() {
    pollRefresh();
  }

  const pollTimer = setInterval(pollRefresh, POLL_INTERVAL_MS);
  if (pollTimer && typeof pollTimer.unref === 'function') pollTimer.unref();

  const tickTimer = setInterval(tick, TICK_INTERVAL_MS);
  if (tickTimer && typeof tickTimer.unref === 'function') tickTimer.unref();

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', onFocus);

  fetchTimers();

  function destroy() {
    clearInterval(pollTimer);
    clearInterval(tickTimer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
  }

  return { el, destroy };
}
